// src/pages/Dashboard.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiSearch, FiCamera, FiX } from 'react-icons/fi';
import { FaHardHat, FaTicketAlt } from 'react-icons/fa';
import { QrReader } from 'react-qr-reader';

import { useIsMobile } from '../hooks/useIsMobile';
import { AuthContext } from '../context/AuthContext';
import TicketQueue from '../components/TicketQueue'; 
import TasksRequiringAttention from '../components/TasksRequiringAttention';
import SearchBar from '../components/SearchBar'; // ✅ Import shared component

// =================================================================================
// QR SCANNER MODAL
// =================================================================================
const ScanQRModal = ({ isOpen, onClose, onScan }) => {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" style={{ zIndex: 2000 }} onClick={onClose}>
            <div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Scan Inventory QR</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ padding: '0', background: 'black', position: 'relative', minHeight: '300px' }}>
                    <QrReader
                        onResult={(result, error) => {
                            if (result) {
                                onScan(result?.text);
                            }
                        }}
                        constraints={{ facingMode: 'environment' }}
                        containerStyle={{ width: '100%', height: '100%' }}
                        videoStyle={{ objectFit: 'cover' }}
                    />
                    {/* Visual Overlay Guide */}
                    <div style={{
                        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                        width: '200px', height: '200px', border: '2px solid rgba(255,255,255,0.7)', borderRadius: '12px',
                        boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)', pointerEvents: 'none'
                    }}></div>
                    <div style={{ position: 'absolute', bottom: '20px', width: '100%', textAlign: 'center', color: 'white', textShadow: '0 1px 2px black' }}>
                        Point camera at a code
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary btn-block" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

const isTicketOverdue = (ticket, appointments) => {
    if (!ticket || ['Closed', 'Pending', 'Waiting On Client', 'Waiting On Parts'].includes(ticket.status)) { return false; }
    const now = new Date(); 
    const twoDaysInMs = 2 * 24 * 60 * 60 * 1000;
    const futureAppointments = (appointments || [])
        .filter(app => { 
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId; 
            return ticketId === ticket._id && new Date(app.startTime) > now; 
        })
        .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const soonestFutureAppointment = futureAppointments[0];
    if (!soonestFutureAppointment) { 
        const createdAt = new Date(ticket.createdAt);
        if (now.getTime() - createdAt.getTime() > twoDaysInMs) { return true; } 
    }
    const allAppointmentsForTicket = (appointments || [])
        .filter(app => { 
            const ticketId = typeof app.ticketId === 'object' ? app.ticketId._id : app.ticketId; 
            return ticketId === ticket._id; 
        })
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));
    if (allAppointmentsForTicket.length > 0) { 
        const latestAppointmentTime = new Date(allAppointmentsForTicket[0].startTime); 
        const lastUpdatedTime = new Date(ticket.updatedAt || ticket.createdAt); 
        if (now.getTime() - latestAppointmentTime.getTime() > twoDaysInMs && lastUpdatedTime.getTime() < latestAppointmentTime.getTime()) { return true; } 
    }
    return false;
};

// Helper: Check Sub-ticket Activity
const hasActiveSubTicket = (parentTicketId, allTickets) => {
    if (!allTickets || !parentTicketId) return false;
    return allTickets.some(t => {
        const pId = typeof t.parentId === 'object' ? t.parentId?._id : t.parentId;
        return pId === parentTicketId && t.activeCheckIns && t.activeCheckIns.length > 0;
    });
};

const Card = ({ title, children }) => ( <div className="card"><div className="card-header"><h3 className="card-title">{title}</h3></div><div className="card-body">{children}</div></div> );

const MyTicketQueue = ({ user, tickets, technicians, appointments, navigate }) => {
    const myTechnicianProfile = useMemo(() => { 
        if (!user || !user.id || !technicians) return null; 
        return technicians.find(t => { 
            if (!t.userId) return false; 
            const techUserId = typeof t.userId === 'object' ? t.userId._id : t.userId; 
            return techUserId === user.id; 
        }); 
    }, [technicians, user]);

    const sortedQueue = useMemo(() => {
        if (!myTechnicianProfile) return [];
        const priorityOrder = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };

        const myOpenTickets = tickets.filter(ticket =>
            !ticket.parentId &&
            ticket.assignedTechnicianIds.includes(myTechnicianProfile._id) &&
            ticket.status !== 'Closed'
        );

        return myOpenTickets.sort((a, b) => {
            const aCheckedIn = a.activeCheckIns?.some(ci => ci.technicianId === myTechnicianProfile._id);
            const bCheckedIn = b.activeCheckIns?.some(ci => ci.technicianId === myTechnicianProfile._id);
            if (aCheckedIn && !bCheckedIn) return -1;
            if (bCheckedIn && !aCheckedIn) return 1;

            if (a.status === 'In Progress' && b.status !== 'In Progress') return -1;
            if (b.status === 'In Progress' && a.status !== 'In Progress') return 1;

            const priorityA = priorityOrder[a.priority] || 5;
            const priorityB = priorityOrder[b.priority] || 5;
            if (priorityA !== priorityB) return priorityA - priorityB;
            
            return new Date(a.createdAt) - new Date(b.createdAt);
        });
    }, [tickets, myTechnicianProfile]);

    return ( 
        <div className="card">
            <div className="card-header"><h3 className="card-title">My Ticket Queue</h3></div>
            <div className="card-body">
                <TicketQueue 
                    tickets={sortedQueue.slice(0, 10)} 
                    allTickets={tickets}
                    appointments={appointments}
                    onTicketClick={(id) => navigate(`/tickets/${id}`)}
                    highlightTechnicianId={myTechnicianProfile?._id}
                    emptyMessage="You have no tickets assigned to you."
                />
            </div>
        </div> 
    );
};

const QuickStats = ({ stats }) => ( <Card title="Quick Stats"><div className="quick-stats-grid">{stats.map(stat => ( <div key={stat.label} className="stat-item"><p className={`stat-value ${stat.colorClass}`}>{stat.value}</p><p className="stat-label">{stat.label}</p></div> ))}</div></Card> );
const RecentActivity = ({ activities }) => ( <Card title="Recent Activity"><div className="activity-list">{activities.map((activity, index) => ( <div key={index} className="activity-item"><p className="activity-description">{activity.description}</p><p className="activity-meta">{activity.user} • {activity.time}</p></div> ))}</div></Card> );
const AnimatedSubtitle = ({ text }) => { const [displayedText, setDisplayedText] = useState(''); useEffect(() => { let isMounted = true; const intervalId = setInterval(() => { if (isMounted) { setDisplayedText(currentText => { if (currentText.length < text.length) { return text.substring(0, currentText.length + 1); } clearInterval(intervalId); return currentText; }); } }, 50); return () => { isMounted = false; clearInterval(intervalId); }; }, [text]); return <p className="page-subtitle">{displayedText}</p>; };

// --- MAIN COMPONENT ---

const Dashboard = ({ tickets, communities, sites, inventory, technicians, appointments }) => { 
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const { user } = useContext(AuthContext);
    const [shouldAnimate, setShouldAnimate] = useState(false);

    // --- QR Scanner State ---
    const [isScannerOpen, setIsScannerOpen] = useState(false);

    const isClient = useMemo(() => user?.roles?.includes('Client'), [user]);
    useEffect(() => {
        const hasAnimated = sessionStorage.getItem('dashboardAnimationPlayed');
        if (!hasAnimated) {
            setShouldAnimate(true);
            sessionStorage.setItem('dashboardAnimationPlayed', 'true');
        }
    }, []);

    // --- Scan Handler ---
    const handleScan = (data) => {
        if (!data) return;
  
        let targetPath = '';
        const match = data.match(/(?:checkout|checkout-unit)\/([a-zA-Z0-9]+)/);
        
        if (match && match[1]) {
            const id = match[1];
            const isUnit = data.includes('checkout-unit');
            
            if (isUnit) {
                targetPath = `/inventory/checkout-unit/${id}`;
            } else {
                targetPath = `/inventory/checkout/${id}`;
            }
        } else {
            // Fallback for raw ID
            targetPath = `/inventory/checkout-unit/${data}`;
        }
  
        setIsScannerOpen(false);
        navigate(targetPath);
    };

    const { allTasksForDashboard, recentActivities, quickStatsData } = useMemo(() => {
        const ticketTasks = tickets
            .filter(ticket => ticket.status !== 'Closed' && !ticket.parentId)
            .map(ticket => ({ 
                id: ticket._id, 
                title: ticket.subject, 
                company: ticket.client, 
                status: ticket.status, 
                priority: ticket.priority, 
                type: 'Tickets', 
                createdAt: ticket.createdAt, 
                isOverdue: isTicketOverdue(ticket, appointments),
                activeCheckIns: ticket.activeCheckIns,
                subTicketActive: hasActiveSubTicket(ticket._id, tickets) 
            }));
        
        // Inventory Tasks mapping...
        const inventoryTasks = isClient ? [] : inventory
            .filter(item => {
                if (item.reorderLevel === null || item.reorderLevel === undefined) return false;
                if (item.quantity > item.reorderLevel) return false;
                if (item.ownerType === 'Community') return false; 
                if (item.ownerType === 'Site') {
                    const site = (sites || []).find(s => s._id === item.ownerId);
                    return site && site.type === 'Office';
                }
                return false;
            })
            .map(item => { 
                let ownerName = 'Unknown Location';
                let ownerIdKey = 'siteId'; 

                if (item.ownerType === 'Community') {
                    const community = communities.find(c => c._id === item.ownerId);
                    if (community) {
                        ownerName = community.name;
                        ownerIdKey = 'communityId';
                    }
                } else if (item.ownerType === 'Site') {
                    const site = (sites || []).find(s => s._id === item.ownerId);
                    if (site) {
                        ownerName = site.name;
                        ownerIdKey = 'siteId';
                    }
                }

                if (ownerName === 'Unknown Location') return null;

                return { 
                    id: item._id, 
                    title: `Low Stock: ${item.name} (${item.sku})`, 
                    company: ownerName, 
                    status: `Needs Reorder (Qty: ${item.quantity})`, 
                    priority: 'High', 
                    type: 'Inventory', 
                    createdAt: item.lastUpdated, 
                    ownerId: item.ownerId, 
                    ownerIdKey: ownerIdKey 
            }; 
            })
            .filter(Boolean); 
        
        const allTasks = [...ticketTasks, ...inventoryTasks];
        const activities = tickets.slice(0, 5).map(ticket => ({ description: `New ticket: "${ticket.subject}" for ${ticket.client}`, user: 'System', time: ticket.createdAt ? new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A' }));
        const stats = [ { value: tickets.filter(t => t.status === 'Open').length, label: 'Open Tickets', colorClass: 'stat-blue' }, { value: inventoryTasks.length, label: 'Low Stock Items', colorClass: 'stat-orange' }, { value: communities.length, label: 'Communities', colorClass: 'stat-red' }, { value: technicians.filter(t => t.status === 'Active').length, label: 'Active Technicians', colorClass: 'stat-green' }, ];
        return { allTasksForDashboard: allTasks, recentActivities: activities, quickStatsData: stats };
    }, [tickets, communities, sites, inventory, technicians, appointments, isClient]);

    const canPerformActions = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
        [user]
    );

    const isTechnician = useMemo(() => {
        if (!user || !technicians) return false;
        return technicians.some(t => (t.userId?._id || t.userId) === user.id);
    }, [user, technicians]);

    return (
        <div className="page-container">
            {/* ✅ Render Scanner Modal */}
            <ScanQRModal 
                isOpen={isScannerOpen} 
                onClose={() => setIsScannerOpen(false)} 
                onScan={handleScan} 
            />

            <div className="page-header">
                <div>
                    <h2 className="page-title">Dashboard</h2>
                    {shouldAnimate && !isMobile ? (
                        <AnimatedSubtitle text={`Welcome back, ${user?.name || 'User'}!`} />
                    ) : (
                        <p className="page-subtitle">{`Welcome back, ${user?.name || 'User'}!`}</p>
                    )}
                </div>
                
                {/* ✅ UPDATED ACTIONS: Show Scan for everyone (if not Client), New Ticket for Admins */}
                {!isClient && (
                    <div className="page-actions dashboard-actions">
                        {/* ✅ ONLY SHOW ON MOBILE */}
                        {isMobile && (
                            <button className="btn btn-secondary" onClick={() => setIsScannerOpen(true)}> 
                                <FiCamera className="btn-icon" /> Scan Inventory 
                            </button>
                        )}
                        
                        {canPerformActions && (
                            <button className="btn btn-primary" onClick={() => navigate('/tickets', { state: { action: 'new' } })}> 
                                New Ticket 
                            </button>
                        )}
                    </div>
                )}
            </div>

            {isMobile ? (
                <div className="mobile-dashboard-list-container">
                    {/* ✅ USE SHARED COMPONENT INSTEAD OF LOCAL LOGIC */}
                    <SearchBar 
                        tickets={tickets} 
                        communities={communities} 
                        technicians={technicians} 
                        inventory={inventory} 
                        sites={sites} 
                    />

                    {isTechnician && <MyTicketQueue user={user} tickets={tickets} technicians={technicians} appointments={appointments} navigate={navigate} />}
                    
                    {/* Only show Tasks/Activity if Admin/Giga Admin */}
                    {canPerformActions && <TasksRequiringAttention tasks={allTasksForDashboard} navigate={navigate} technicians={technicians} />}
                    {canPerformActions && <QuickStats stats={quickStatsData} />}
                    {canPerformActions && <RecentActivity activities={recentActivities} />}
                </div>
            ) : (
                <>
                    {/* Basic Layout for Technicians (Ticket Queue Only) */}
                    {!canPerformActions ? (
                        <div className="dashboard-grid">
                            <div className="main-column">
                                {isTechnician ? (
                                    <MyTicketQueue user={user} tickets={tickets} technicians={technicians} appointments={appointments} navigate={navigate} />
                                ) : (
                                    <div className="no-tasks-message">
                                        <p>Your account is not linked to a technician profile. Please contact an admin.</p>
                                    </div>
                                )}
                            </div>
                            <div className="side-column"></div>
                        </div>
                    ) : (
                        /* Full Layout for Admins */
                        <div className="dashboard-grid">
                            <div className="main-column">
                                <TasksRequiringAttention tasks={allTasksForDashboard} navigate={navigate} technicians={technicians} />
                                {isTechnician && <MyTicketQueue user={user} tickets={tickets} technicians={technicians} appointments={appointments} navigate={navigate} />}
                            </div>
                            <div className="side-column">
                                <QuickStats stats={quickStatsData} />
                                <RecentActivity activities={recentActivities} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Dashboard;